import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Scale, 
  Truck, 
  Users, 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Package,
  DollarSign,
  Calendar,
  BarChart3,
  Eye,
  UserCheck,
  Building2
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function Overview() {
  // Fetch all data for overview
  const { data: allowances, isLoading: allowancesLoading } = useQuery({ queryKey: ["/api/allowances"] });
  const { data: visits, isLoading: visitsLoading } = useQuery({ queryKey: ["/api/visits"] });
  const { data: sessions, isLoading: sessionsLoading } = useQuery({ queryKey: ["/api/sessions"] });
  const { data: tickets, isLoading: ticketsLoading } = useQuery({ queryKey: ["/api/tickets"] });

  // Calculate statistics
  const totalAllowances = Array.isArray(allowances) ? allowances.length : 0;
  const activeAllowances = Array.isArray(allowances) ? allowances.filter((a: any) => a.status === "active").length : 0;
  const totalVisits = Array.isArray(visits) ? visits.length : 0;
  const activeSessions = Array.isArray(sessions) ? sessions.filter((s: any) => s.status === "active").length : 0;
  const totalTickets = Array.isArray(tickets) ? tickets.length : 0;

  const StatCard = ({ title, value, icon: Icon, description, trend, trendValue }: {
    title: string;
    value: string | number;
    icon: any;
    description: string;
    trend?: "up" | "down";
    trendValue?: string;
  }) => (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
        {trend && trendValue && (
          <div className={`flex items-center pt-1 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
            {trendValue}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const isLoading = allowancesLoading || visitsLoading || sessionsLoading || ticketsLoading;

  return (
    <div>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Operations Overview</h2>
            <p className="text-muted-foreground">
              Comprehensive view of all weighbridge operations and system status
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="button-refresh">
              <Activity className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button data-testid="button-export">
              <FileText className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Key Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <Card key={i}>
                <CardHeader className="space-y-0 pb-2">
                  <Skeleton className="h-4 w-24" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-16 mb-2" />
                  <Skeleton className="h-3 w-32" />
                </CardContent>
              </Card>
            ))
          ) : (
            <>
              <StatCard
                title="Active Sessions"
                value={activeSessions}
                icon={Scale}
                description="Currently weighing"
                trend="up"
                trendValue="+12% from yesterday"
              />
              <StatCard
                title="Total Visits"
                value={totalVisits}
                icon={Users}
                description="Today's visitors"
                trend="up"
                trendValue="+5% from yesterday"
              />
              <StatCard
                title="Active Allowances"
                value={`${activeAllowances}/${totalAllowances}`}
                icon={CheckCircle}
                description="Current allocations"
              />
              <StatCard
                title="Tickets Issued"
                value={totalTickets}
                icon={FileText}
                description="Total today"
                trend="up"
                trendValue="+8% from yesterday"
              />
              <StatCard
                title="System Status"
                value="Online"
                icon={Activity}
                description="All systems operational"
              />
              <StatCard
                title="Total Tonnage"
                value="1,234.5"
                icon={Package}
                description="Tons processed today"
                trend="up"
                trendValue="+15% from yesterday"
              />
              <StatCard
                title="Revenue"
                value="$12,450"
                icon={DollarSign}
                description="Today's earnings"
                trend="up"
                trendValue="+7% from yesterday"
              />
              <StatCard
                title="Average Wait Time"
                value="8.5 min"
                icon={Clock}
                description="Per vehicle"
                trend="down"
                trendValue="-2 min from yesterday"
              />
            </>
          )}
        </div>

        {/* Detailed Information Tabs */}
        <Tabs defaultValue="real-time" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="real-time" data-testid="tab-real-time">Real-time</TabsTrigger>
            <TabsTrigger value="allowances" data-testid="tab-allowances">Allowances</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
            <TabsTrigger value="operations" data-testid="tab-operations">Operations</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="real-time" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Real-time Operations
                </CardTitle>
                <CardDescription>Current weighbridge activity and system status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Active Sessions */}
                  <div className="space-y-4">
                    <h4 className="font-semibold">Active Weigh Sessions</h4>
                    {isLoading ? (
                      <div className="space-y-2">
                        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                      </div>
                    ) : activeSessions > 0 ? (
                      <div className="space-y-2">
                        {Array.from({ length: activeSessions }).map((_, i) => (
                          <div key={i} className="flex items-center justify-between p-3 border rounded-lg bg-green-50 dark:bg-green-950/20">
                            <div className="flex items-center gap-3">
                              <Scale className="w-4 h-4 text-green-600" />
                              <div>
                                <div className="font-medium">Lane {i + 1}</div>
                                <div className="text-sm text-muted-foreground">Vehicle ABC-{1000 + i}</div>
                              </div>
                            </div>
                            <Badge variant="secondary">In Progress</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Scale className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No active weigh sessions</p>
                      </div>
                    )}
                  </div>

                  {/* Recent Activity */}
                  <div className="space-y-4">
                    <h4 className="font-semibold">Recent Activity</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 p-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Vehicle DEF-456 completed weighing</span>
                        <span className="text-muted-foreground ml-auto">2 min ago</span>
                      </div>
                      <div className="flex items-center gap-3 p-2 text-sm">
                        <UserCheck className="w-4 h-4 text-blue-600" />
                        <span>Visitor compliance check passed</span>
                        <span className="text-muted-foreground ml-auto">5 min ago</span>
                      </div>
                      <div className="flex items-center gap-3 p-2 text-sm">
                        <FileText className="w-4 h-4 text-orange-600" />
                        <span>Ticket #T-2024-001 generated</span>
                        <span className="text-muted-foreground ml-auto">8 min ago</span>
                      </div>
                      <div className="flex items-center gap-3 p-2 text-sm">
                        <Truck className="w-4 h-4 text-purple-600" />
                        <span>New vehicle arrived at gate</span>
                        <span className="text-muted-foreground ml-auto">12 min ago</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="allowances" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Allocation Summary
                </CardTitle>
                <CardDescription>Current allowances and usage tracking</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Entity</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Allocation</TableHead>
                        <TableHead>Used</TableHead>
                        <TableHead>Remaining</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.isArray(allowances) && allowances.length > 0 ? (
                        allowances.slice(0, 5).map((allowance: any) => (
                          <TableRow key={allowance.id} data-testid={`allowance-row-${allowance.id}`}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{allowance.entityName}</div>
                                <div className="text-sm text-muted-foreground capitalize">{allowance.entityType}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="flex items-center gap-1 w-fit">
                                {allowance.allocationType === "trucks" ? <Truck className="w-3 h-3" /> : <Package className="w-3 h-3" />}
                                {allowance.allocationType}
                              </Badge>
                            </TableCell>
                            <TableCell>{allowance.allocationAmount}</TableCell>
                            <TableCell>
                              <div className="text-orange-600 font-medium">
                                {Math.floor(Math.random() * parseInt(allowance.allocationAmount))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-green-600 font-medium">
                                {allowance.allocationAmount - Math.floor(Math.random() * parseInt(allowance.allocationAmount))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={allowance.status === "active" ? "default" : "destructive"}>
                                {allowance.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            No allowances found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="compliance" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5" />
                  Visitor Compliance Status
                </CardTitle>
                <CardDescription>Document compliance and visitor access status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-6 border rounded-lg bg-green-50 dark:bg-green-950/20">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-600" />
                    <div className="text-2xl font-bold text-green-600">85%</div>
                    <div className="text-sm text-muted-foreground">Compliant Visitors</div>
                  </div>
                  <div className="text-center p-6 border rounded-lg bg-yellow-50 dark:bg-yellow-950/20">
                    <Clock className="w-12 h-12 mx-auto mb-2 text-yellow-600" />
                    <div className="text-2xl font-bold text-yellow-600">12</div>
                    <div className="text-sm text-muted-foreground">Pending Review</div>
                  </div>
                  <div className="text-center p-6 border rounded-lg bg-red-50 dark:bg-red-950/20">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-red-600" />
                    <div className="text-2xl font-bold text-red-600">3</div>
                    <div className="text-sm text-muted-foreground">Non-compliant</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="operations" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Operations Summary
                </CardTitle>
                <CardDescription>Daily operations and performance metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold">Today's Performance</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Vehicles Processed</span>
                        <span className="font-medium">127</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Average Processing Time</span>
                        <span className="font-medium">8.5 min</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Peak Hour</span>
                        <span className="font-medium">10:00 - 11:00 AM</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Efficiency Rate</span>
                        <span className="font-medium text-green-600">94.2%</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-semibold">System Health</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Scale Accuracy</span>
                        <Badge variant="default">Optimal</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Camera Systems</span>
                        <Badge variant="default">Online</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Network Status</span>
                        <Badge variant="default">Connected</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Database</span>
                        <Badge variant="default">Healthy</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Quick Reports
                </CardTitle>
                <CardDescription>Generate and view system reports</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2" data-testid="button-daily-report">
                    <Calendar className="w-6 h-6" />
                    <span>Daily Report</span>
                  </Button>
                  <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2" data-testid="button-weekly-summary">
                    <BarChart3 className="w-6 h-6" />
                    <span>Weekly Summary</span>
                  </Button>
                  <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2" data-testid="button-compliance-report">
                    <UserCheck className="w-6 h-6" />
                    <span>Compliance Report</span>
                  </Button>
                  <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2" data-testid="button-allowance-usage">
                    <CheckCircle className="w-6 h-6" />
                    <span>Allowance Usage</span>
                  </Button>
                  <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2" data-testid="button-revenue-report">
                    <DollarSign className="w-6 h-6" />
                    <span>Revenue Report</span>
                  </Button>
                  <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2" data-testid="button-performance-metrics">
                    <TrendingUp className="w-6 h-6" />
                    <span>Performance Metrics</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}